import React, { useEffect } from 'react';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { QueryBuilderQuery, SearchBackend, SortOrder } from '@ardoq/api-types';
import { SearchResultTable } from '../../../components/SearchResultTable/SearchResultTable';
import { findRelevantFieldsFromQuery } from '../../../components/SearchResultTable/utils';
import { SEARCH_RESULTS_PER_PAGE } from '../../../search/types';
import { advancedSearchTable$ } from './advancedSearchTable$';
import {
  pageWasSet,
  queryWasSet,
  searchParamsWereSet,
  selectedFieldsWereSet,
} from './actions';
import { queryAdvancedSearch } from '../../../search/AdvancedSearch/actions';
import { AdvancedSearchErrorMessage } from '../../../search/AdvancedSearch/AdvancedSearchErrorMessage';
import {
  AdvancedSearchTableCommands,
  AdvancedSearchTableStreamShape,
} from './types';
import { combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { isEqual } from 'lodash';
import { PayloadQueryAdvancedSearch } from '../../../search/AdvancedSearch/types';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import type { PermissionContext } from '@ardoq/access-control';
import type { I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';

type OwnProps = {
  query: QueryBuilderQuery;
};

type ContainerProps = OwnProps &
  AdvancedSearchTableStreamShape & {
    locale: Locale;
    i18nLocale: I18nLocale;
    permissionContext: PermissionContext;
  };

const commands: AdvancedSearchTableCommands = {
  selectPage: (page: number) => dispatchAction(pageWasSet(page)),
  onSortChange: (
    sortBy: string | undefined,
    sortOrder: SortOrder | undefined
  ) => dispatchAction(searchParamsWereSet({ sortBy, sortOrder })),
  selectFields: (fields: Array<string>) =>
    dispatchAction(selectedFieldsWereSet(fields)),
};

const AdvancedSearchTableWithLoaderBase = ({
  advancedSearchStreamData: {
    resultFields,
    numberFormatByFieldName,
    results,
    total,
    searchError,
    ruleErrorMessages,
  },
  query,
  page,
  isSearching,
  searchParams,
  selectedFields,
  locale,
  i18nLocale,
  permissionContext,
}: ContainerProps) => {
  const rules = query?.rules[1];

  useEffect(() => {
    dispatchAction(queryWasSet(query));
  }, [query]);

  if (searchError) {
    return (
      <AdvancedSearchErrorMessage
        searchError={searchError}
        ruleErrorMessages={ruleErrorMessages}
        i18nLocale={i18nLocale}
      />
    );
  }

  return (
    <SearchResultTable
      queryRelevantFieldsNames={rules ? findRelevantFieldsFromQuery(rules) : []}
      selectedFieldNames={selectedFields}
      onFieldSelect={commands.selectFields}
      onPageSelect={commands.selectPage}
      onSortChange={commands.onSortChange}
      sortBy={searchParams?.sortBy}
      sortOrder={searchParams?.sortOrder}
      fields={resultFields}
      numberFormatByFieldName={numberFormatByFieldName}
      results={results}
      page={page}
      perPage={searchParams.size ?? SEARCH_RESULTS_PER_PAGE}
      total={total}
      isLoading={isSearching}
      intercomTarget="advanced search results"
      locale={locale}
      i18nLocale={i18nLocale}
      permissionContext={permissionContext}
    />
  );
};

export const doQuerySearch$ = advancedSearchTable$.pipe(
  map(({ queryBuilderRules, searchParams }) => ({
    queryBuilderRules,
    searchBackend: SearchBackend.ADVANCED_SEARCH,
    ...searchParams,
  })),
  distinctUntilChanged<PayloadQueryAdvancedSearch>(isEqual),
  tap(advancedSearchPayload => {
    if (advancedSearchPayload.queryBuilderRules) {
      dispatchAction(queryAdvancedSearch(advancedSearchPayload));
    }
  })
);

const viewModel$ = combineLatest({
  advancedSearchTable: advancedSearchTable$,
  doQuerySearch$,
  locale: locale$,
  i18nLocale: i18nLocale$,
  permissionContext: currentUserPermissionContext$,
}).pipe(
  map(({ advancedSearchTable, locale, i18nLocale, permissionContext }) => ({
    ...advancedSearchTable,
    locale,
    i18nLocale,
    permissionContext,
  }))
);

export const AdvancedSearchTableWithLoader = connect<
  ContainerProps,
  AdvancedSearchTableStreamShape & {
    locale: Locale;
    i18nLocale: I18nLocale;
    permissionContext: PermissionContext;
  }
>(AdvancedSearchTableWithLoaderBase, viewModel$);
